import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePortalAuth } from '../contexts/PortalAuthContext';
import { useAgreementGate } from '../hooks/useAgreementGate';
import FarmhandChat from './Portal/FarmhandChat';
import PlatformAgreementModal from './Portal/PlatformAgreementModal';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const PortalProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = usePortalAuth();
  const { loading: agreementLoading, accepted, markAccepted } = useAgreementGate();

  // Wait for auth check to complete before redirecting
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  // Wait for agreement check
  if (agreementLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Show agreement modal if not accepted
  if (!accepted) {
    return <PlatformAgreementModal onAccepted={markAccepted} />;
  }

  return (
    <>
      <OfflineBanner />
      {children}
      <FarmhandChat />
    </>
  );
};

/** Subtle offline indicator — shows when device has no connectivity */
const OfflineBanner: React.FC = () => {
  const [offline, setOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: '#78716c', color: '#fff', textAlign: 'center',
      padding: '6px 12px', fontSize: 13, fontWeight: 500,
    }}>
      You're offline — notes will sync when connected
    </div>
  );
};

export default PortalProtectedRoute;
